// copy: not-copy — clipboard plumbing: CSS class names and a text extractor for the copy button; no words a person reads
/**
 * chunkCopy — one-click copy affordance for copyable CHUNKS inside chat
 * bubbles (owner directive 2026-08-17): email drafts rendered as
 * <blockquote>, code rendered as <pre><code>. The user copies the clean
 * chunk — no quote markers, no surrounding prose — ready to paste into
 * Gmail/an editor.
 *
 * Why DOM-layer decoration instead of emitting buttons in the HTML string:
 * both converter pipelines sanitize their output (and the code-block stash
 * regex assumes the exact <pre><code…></code></pre> shape — R7,
 * markdownProcessor.ts), so buttons in the HTML string would be stripped or
 * would break the stash. Decorating the rendered DOM sidesteps both
 * sanitizers, works identically for message.html and converted-text bubbles,
 * and survives `dangerouslySetInnerHTML` re-renders because the caller
 * re-runs decoration after every render (idempotent via a data marker; a
 * re-set innerHTML wipes the old button AND the marker together, so the next
 * pass re-decorates cleanly).
 *
 * House conventions honored: same copy SVG + lowercase labels as
 * CopyIconButton (GlobalChatSystem.tsx bubble footer), clipboard via the
 * shared copyToClipboard helper (secure-context API + textarea fallback),
 * accent-on-hover styling supplied by styles.ts under `.chunk-copy-btn`.
 */

import { copyToClipboard } from '../common/CopyableValue/utils';

export const CHUNK_COPY_BTN_CLASS = 'chunk-copy-btn';
export const CHUNK_COPY_DECORATED_ATTR = 'data-chunk-copy-decorated';
export const CHUNK_COPY_HOST_CLASS = 'chunk-copy-host';
const COPIED_FEEDBACK_MS = 1400;

const COPY_ICON_SVG =
  '<svg viewBox="0 0 24 24" aria-hidden="true">'
  + '<rect x="9" y="9" width="11" height="11" rx="2" ry="2" />'
  + '<path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />'
  + '</svg>';

const CHECK_ICON_SVG =
  '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M20 6L9 17l-5-5" /></svg>';

/**
 * The clean text a chunk copies. <pre>: the code content only (never the
 * button's own label). <blockquote>: innerText minus any stray leading
 * quote markers a nested `>>` left behind.
 */
export function extractChunkCopyText(el: HTMLElement): string {
  const clone = el.cloneNode(true) as HTMLElement;
  clone.querySelectorAll(`.${CHUNK_COPY_BTN_CLASS}`).forEach((b) => b.remove());
  // Rendered <br> line structure must survive into the copied text —
  // innerText handles that in real browsers; the textContent fallback
  // (jsdom, detached nodes) needs the <br>s materialized first.
  clone.querySelectorAll('br').forEach((br) => br.replaceWith(clone.ownerDocument.createTextNode('\n')));
  const textOf = (node: HTMLElement): string => {
    const viaInnerText = (node as { innerText?: string }).innerText;
    return typeof viaInnerText === 'string' ? viaInnerText : (node.textContent || '');
  };
  if (el.tagName === 'PRE') {
    const code = clone.querySelector('code');
    return textOf((code as HTMLElement) || clone).replace(/\n$/, '');
  }
  return textOf(clone)
    .split('\n')
    .map((line) => line.replace(/^\s*>+\s?/, ''))
    .join('\n')
    .trim();
}

function buildButton(host: HTMLElement, doc: Document): HTMLButtonElement {
  const btn = doc.createElement('button');
  btn.type = 'button';
  btn.className = CHUNK_COPY_BTN_CLASS;
  btn.setAttribute('aria-label', 'copy text');
  btn.title = 'copy';
  btn.innerHTML = COPY_ICON_SVG;
  btn.addEventListener('click', (e) => {
    e.preventDefault();
    e.stopPropagation();
    const text = extractChunkCopyText(host);
    if (!text) return;
    copyToClipboard(
      text,
      () => {
        btn.classList.add('copied');
        btn.innerHTML = CHECK_ICON_SVG;
        btn.setAttribute('aria-label', 'copied');
        window.setTimeout(() => {
          btn.classList.remove('copied');
          btn.innerHTML = COPY_ICON_SVG;
          btn.setAttribute('aria-label', 'copy text');
        }, COPIED_FEEDBACK_MS);
      },
      () => {
        /* copy failed (blocked clipboard) — leave the button untouched */
      },
    );
  });
  return btn;
}

/**
 * Decorate every un-decorated <pre> and <blockquote> under `root` with a
 * copy button. Idempotent per element; safe to call after every render.
 * Skips chunks inside the pending/stopped widget wrappers (status chrome,
 * not content). Returns the number of chunks decorated in this pass.
 */
export function decorateCopyableChunks(root: HTMLElement | null | undefined): number {
  if (!root || typeof root.querySelectorAll !== 'function') return 0;
  const doc = root.ownerDocument || document;
  let decorated = 0;
  root.querySelectorAll<HTMLElement>('pre, blockquote').forEach((el) => {
    if (el.getAttribute(CHUNK_COPY_DECORATED_ATTR) === 'true') return;
    if (el.closest('.stopped-widget-wrapper')) return;
    if (el.closest(`[data-source-tag]`)?.getAttribute('data-source-tag') === 'pending') return;
    // A blockquote nested inside a pre (or another blockquote) copies with
    // its parent — one button per outermost chunk.
    if (el.parentElement?.closest('pre, blockquote')) {
      el.setAttribute(CHUNK_COPY_DECORATED_ATTR, 'true');
      return;
    }
    el.setAttribute(CHUNK_COPY_DECORATED_ATTR, 'true');
    el.classList.add(CHUNK_COPY_HOST_CLASS);
    el.appendChild(buildButton(el, doc));
    decorated += 1;
  });
  return decorated;
}
